// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useState } from "react";
// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import axios from "axios.js";
import GoogleMapReact from "google-map-react";
import AppConfig from "appConfig";
import DataTable from "theme/Tables/DataTable";

function AgentFootPrints(payload) {
  const { campaignReference } = payload;

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [footprints, setFootprints] = useState([]);
  const [agents, setAgents] = useState([]);
  const [mapZoom, setMapZoom] = useState(11);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [agentFootPrints, setAgentFootPrints] = useState([]);
  const [agentFootPrintHistory, setAgentFootPrintHistory] = useState([]);
  const [selectedAgentName, setSelectedAgentName] = useState(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);

  const tableColumns = [
    { Header: "Agent", accessor: "agent", align: "left" },
    { Header: "Time", accessor: "time", align: "center" },
    { Header: "Location", accessor: "location", align: "center" },
    { Header: "History", accessor: "history", align: "right" },
  ];

  function buildRows(data) {
    const rows = [];
    data.map((row) => {
      rows.push({
        // gps: <Icon color="error">location_on</Icon>,
        agent: row.agent,
        time: row.created,
        location: (
          <a
            rel="noreferrer"
            target="_blank"
            href={`https://maps.google.com?q=${row.latitude},${row.longitude}`}
          >
            <Icon color="error">location_on</Icon>
          </a>
        ),
        history: (
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            iconOnly
            onClick={() => {
              setSelectedAgentName(row.agent);
              fetchAgentFootprints(row.reference);
            }}
          >
            <Icon>east</Icon>
          </MDButton>
        ),
      });
      return null;
    });

    return rows;
  }

  const tableFootPrintColumns = [
    { Header: "Time", accessor: "time", align: "left" },
    { Header: "Location", accessor: "location", align: "right" },
  ];

  function buildHistoryRows(data) {
    const rows = [];
    data.map((row) => {
      rows.push({
        time: row.created,
        location: (
          <a
            rel="noreferrer"
            target="_blank"
            href={`https://maps.google.com?q=${row.latitude},${row.longitude}`}
          >
            <Icon color="error">location_on</Icon>
          </a>
        ),
      });
      return null;
    });

    return rows;
  }

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/campaigns/${campaignReference}/activity/footprints.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setFootprints(result.data.data.footprints);
            const rows = buildRows(result.data.data.footprints);
            setAgents(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  async function fetchAgentFootprints(agentRef) {
    setShowLoading(true);
    await axios
      .get(`portal/campaigns/agent/${agentRef}/footprints.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          if (result.data.data.footprints.length <= 0) {
            setSeverity("error");
            setErrMsg("No footprints recorded today.");
            setOpen(true);
            return;
          }

          setAgentFootPrints(result.data.data.footprints);
          const rows = buildHistoryRows(result.data.data.footprints);
          setAgentFootPrintHistory(rows);

          setShowHistoryDialog(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
          setSelectedAgentName(null);
          setShowHistoryDialog(false);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {pageLoaded && (
        <MDBox mt={5}>
          {agentFootPrints.length > 0 && (
            <Dialog open={showHistoryDialog} fullWidth>
              <DialogTitle>
                <MDBox display="flex" justifyContent="space-between">
                  <MDBox>{selectedAgentName}</MDBox>
                  <MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      iconOnly
                      circular
                      onClick={() => {
                        setSelectedAgentName(null);
                        setShowHistoryDialog(false);
                      }}
                    >
                      <Icon>close</Icon>
                    </MDButton>
                  </MDBox>
                </MDBox>
              </DialogTitle>
              <DialogContent>
                <MDBox mt={1}>
                  <MDBox sx={{ height: "50vh", width: "100%" }}>
                    <GoogleMapReact
                      bootstrapURLKeys={{ key: AppConfig.google_map_key }}
                      defaultCenter={{
                        lat: parseFloat(agentFootPrints[0].latitude),
                        lng: parseFloat(agentFootPrints[0].longitude),
                      }}
                      defaultZoom={18}
                      zoom={17}
                    >
                      {agentFootPrints.map((loc) => (
                        <MDBox
                          lat={parseFloat(loc.latitude)}
                          lng={parseFloat(loc.longitude)}
                          // onClick={() => navigate(`/data-capture/${loc.reference}`)}
                        >
                          <Tooltip
                            title={
                              <MDTypography color="white" variant="caption">
                                {loc.created}
                              </MDTypography>
                            }
                          >
                            <MDTypography fontSize={32}>
                              <Icon color="error">location_on</Icon>
                            </MDTypography>
                          </Tooltip>
                        </MDBox>
                      ))}
                    </GoogleMapReact>
                  </MDBox>
                  <MDBox mt={5} pb={3}>
                    <DataTable
                      table={{ columns: tableFootPrintColumns, rows: agentFootPrintHistory }}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries={false}
                      buttons
                      noEndBorder
                      canSearch={false}
                    />
                  </MDBox>
                </MDBox>
              </DialogContent>
            </Dialog>
          )}
          {footprints.length > 0 ? (
            <MDBox>
              <MDBox>
                <Card>
                  <MDBox p={3}>
                    <MDBox mb={3} display="flex" justifyContent="space-between">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Team Heat Map
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light">
                          Recent team members' recorded locations
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            let pZoom = mapZoom;
                            pZoom -= 1;
                            pZoom = pZoom < 4 ? 4 : pZoom;
                            setMapZoom(pZoom);
                          }}
                        >
                          <Icon>remove</Icon>
                        </MDButton>
                        &nbsp;
                        <MDTypography variant="caption" fontSize={10} fontWeight="light">
                          Zoom
                        </MDTypography>
                        &nbsp;
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            let pZoom = mapZoom;
                            pZoom += 1;
                            pZoom = pZoom > 20 ? 20 : pZoom;
                            setMapZoom(pZoom);
                          }}
                        >
                          <Icon>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox sx={{ height: "75vh", width: "100%" }}>
                      <GoogleMapReact
                        bootstrapURLKeys={{ key: AppConfig.google_map_key }}
                        defaultCenter={{
                          lat: parseFloat(footprints[0].latitude),
                          lng: parseFloat(footprints[0].longitude),
                        }}
                        defaultZoom={mapZoom}
                        zoom={mapZoom}
                      >
                        {footprints.map((loc) => (
                          <MDBox
                            lat={parseFloat(loc.latitude)}
                            lng={parseFloat(loc.longitude)}
                            // onClick={() => navigate(`/data-capture/${loc.reference}`)}
                          >
                            <Tooltip
                              title={
                                <>
                                  <MDTypography color="white" variant="h6" fontSize={12}>
                                    {loc.agent}
                                  </MDTypography>
                                  <MDTypography color="white" variant="caption">
                                    {loc.created}
                                  </MDTypography>
                                </>
                              }
                            >
                              <MDTypography fontSize={32}>
                                <Icon color="error">person_pin_circle</Icon>
                              </MDTypography>
                            </Tooltip>
                          </MDBox>
                        ))}
                      </GoogleMapReact>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
              <MDBox mt={5}>
                <Card>
                  <MDBox pb={3}>
                    <DataTable
                      table={{ columns: tableColumns, rows: agents }}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries={false}
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </Card>
              </MDBox>
            </MDBox>
          ) : (
            <MDBox p={3} align="center">
              <MDTypography variant="subtitle2" fontSize={14} fontWeight="regular">
                No records available
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      )}
    </MDBox>
  );
}

export default AgentFootPrints;
